import React, { useState } from 'react';
import StarRating from './StarRating';

/**
 * Inline Feedback Component - Thumbs up/down or stars for quick feedback
 * @param {string} type - 'thumbs' | 'stars' | 'both' (default: 'thumbs')
 * @param {string|number} currentValue - Current feedback value ('up'|'down'|null or 1-5)
 * @param {function} onSubmit - Callback when feedback submitted (value)
 * @param {string} label - Optional label text
 * @param {string} size - sm, md, lg (default: md)
 * @param {boolean} disabled - Disable interactions (default: false)
 * @param {string} className - Additional CSS classes
 */
const InlineFeedback = ({
  type = 'thumbs',
  currentValue = null,
  onSubmit = null,
  label = null,
  size = 'md',
  disabled = false,
  className = ''
}) => {
  const [localValue, setLocalValue] = useState(currentValue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sizeClasses = {
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  };

  const iconSizes = {
    sm: 'fs-6',
    md: 'fs-5',
    lg: 'fs-4'
  };

  const handleThumbsClick = async (value) => {
    if (disabled || isSubmitting) return;

    const newValue = localValue === value ? null : value; // Toggle off if clicking same thumb
    setLocalValue(newValue);

    if (onSubmit) {
      setIsSubmitting(true);
      try {
        await onSubmit(newValue);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleStarRate = async (rating) => {
    if (disabled || isSubmitting) return;

    setLocalValue(rating);

    if (onSubmit) {
      setIsSubmitting(true);
      try {
        await onSubmit(rating);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const renderThumbs = () => (
    <div className="d-inline-flex align-items-center gap-2">
      <button
        type="button"
        className={`btn ${sizeClasses[size]} ${
          localValue === 'up' ? 'btn-success' : 'btn-outline-secondary'
        }`}
        onClick={() => handleThumbsClick('up')}
        disabled={disabled || isSubmitting}
        title="Good"
      >
        <i className={`fas fa-thumbs-up ${iconSizes[size]}`} />
      </button>
      <button
        type="button"
        className={`btn ${sizeClasses[size]} ${
          localValue === 'down' ? 'btn-danger' : 'btn-outline-secondary'
        }`}
        onClick={() => handleThumbsClick('down')}
        disabled={disabled || isSubmitting}
        title="Needs improvement"
      >
        <i className={`fas fa-thumbs-down ${iconSizes[size]}`} />
      </button>
    </div>
  );

  const renderStars = () => (
    <StarRating
      rating={localValue || 0}
      maxStars={5}
      onRate={handleStarRate}
      readOnly={disabled || isSubmitting}
      size={size}
      showValue={false}
    />
  );

  const renderBoth = () => (
    <div className="d-inline-flex align-items-center gap-3">
      {renderThumbs()}
      <div className="vr" style={{ height: '20px' }} />
      {renderStars()}
    </div>
  );

  const renderFeedback = () => {
    switch (type) {
      case 'stars':
        return renderStars();
      case 'both':
        return renderBoth();
      case 'thumbs':
      default:
        return renderThumbs();
    }
  };

  return (
    <div className={`inline-feedback d-inline-flex align-items-center gap-2 ${className}`}>
      {label && <span className="text-muted small">{label}:</span>}
      {renderFeedback()}
      {isSubmitting && (
        <span className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">Saving...</span>
        </span>
      )}
    </div>
  );
};

export default InlineFeedback;
